import { MetadataFormField } from '@cube3/common/model/schema/resources/metadata-category';
import { Field, FieldType } from '@cube3/forms/src/types/GenericFormTypes';
import { makeData } from '@cube3/forms/src/utils/makeMetadataFields';
import { makeWaterfallSchemaProperties } from './makeSchema';

const getSettingsFieldConfig = (field: MetadataFormField, key: string) => {
  const data = makeData(field);
  const value = data?.[key] ?? field[key];
  const isCheckbox = field.value_type === 'checkbox';

  switch (key) {
    case 'text': // checkbox prop
      return {
        id: 'text',
        type: 'string',
        label: 'description',
        value: value
      };
    case 'placeholder':
      return {
        id: 'placeholder',
        label: key,
        type: 'string',
        placeholder: 'Placeholder...',
        value: value || 'Placeholder...'
      };

    case 'minLength':
    case 'min':
    case 'max':
    case 'step':
      return {
        id: key,
        type: 'number',
        label: key,
        value: value
      };
    case 'maxLength':
      return {
        id: key,
        type: 'number',
        label: isCheckbox ? 'Max choices' : key,
        value: value
      };
    case 'multiline':
      return {
        id: key,
        type: 'number',
        label: key,
        value: value || 2
      };
    case 'pattern':
      return {
        id: 'pattern',
        type: 'string',
        label: key,
        value: value || ''
      };
    case 'options': {

      if (field.value_type === 'waterfall_select') {
        return {
          id: key,
          type: 'textarea',
          label: "Waterfall Options",
          value: JSON.stringify(value, null, 2),
          validate: (val) => {
            try {
              makeWaterfallSchemaProperties(val, { properties: {} })
            } catch (e) {
              return [
                {
                  field: key,
                  code: 'INVALID_CONFIG_JSON',
                  message: "Not valid JSON config",
                  type: 'error' as const
                }
              ];
            }



          }
        }
      }

      const options = data?.options?.map((opt) =>
        typeof opt === 'string'
          ? {
            id: opt,
            value: opt
          }
          : opt
      );
      return {
        id: 'options',
        type: 'multiselect',
        label: key,
        excludedSuggestions: options,
        allowAddingOfNewSuggestions: true,
        value: options,
        // for multiple checkboxes, it expects item.value
        primaryKey: isCheckbox ? 'value' : 'id',
        placeholder: 'Start typing to add options',
        keysListeners: ['Tab', 'Enter'],
        visible: (values) => {
          if (isCheckbox) {
            const dependOn = values.find((v) => v.id === 'multiple');
            return dependOn && dependOn.value;
          }
          return true;
        }
      };
    }
    case 'multiple':
      return {
        id: key,
        type: 'checkbox',
        label: key,
        value: data.multiple || data.options?.length > 0
      };
    case 'yearOnly':
    case 'required':
      return {
        id: key,
        type: 'checkbox',
        label: key,
        value: value
      };
    case 'minDate':
      return {
        id: key,
        type: 'date',
        label: key,
        value: value
      };


    default:
      return {
        id: key,
        type: 'string',
        label: 'label',
        value: ''
      };
  }
};

export const getFieldsFromSchema = (field: MetadataFormField) => {
  let keys = ['required'];
  const type = field.value_type as FieldType;

  switch (type) {
    case 'string':
      keys.push('minLength', 'maxLength', 'pattern');
      break;
    case 'textarea':
      keys.push('minLength', 'maxLength', 'multiline');
      break;
    case 'number':
      keys.push('min', 'max', 'step');
      break;
    case 'multiselect':
    case 'select':
      keys.push('options');
      break;
    case 'checkbox':
      keys.push('multiple', 'options', 'maxLength');
      break;
    case 'date':
      keys.push('yearOnly', 'minDate');
      break;
    case 'expires_at':
      keys.push('minDate');
      break;
    case 'asset':
      keys = keys.filter((k) => k !== 'placeholder');
      break;
    case 'waterfall_select':
      keys.push('options')
      break;
    default:
      break;
  }

  return keys.map(
    (k) => ({ ...getSettingsFieldConfig(field, k), fullWidth: true } as Field)
  );
};
